import { createContext, PropsWithChildren, useContext } from 'react';
import { LegacyIIQApi, LegacyIIQApiOptions } from './legacy-iiq-api';
import { useAuth0 } from '@auth0/auth0-react';
import { getEndpoints, LegacyIIQApiEndpoints } from './get-endpoints';
import Loading from '../../components/Loading/Loading';
import { insureiqPages } from '@harvestiq/constants';
import { useRedirect } from '../Redirect/index';

type LegacyIIQApiContextProps = {
  legacyIIQApi: LegacyIIQApi;
} & LegacyIIQApiEndpoints;

export const LegacyIIQApiContext =
  createContext<LegacyIIQApiContextProps | null>(null);

export function LegacyIIQApiProvider({ children }: PropsWithChildren) {
  const { isLoading, isAuthenticated, getAccessTokenSilently, logout } =
    useAuth0();
  const { setRedirect } = useRedirect();

  const getAuth0AccessToken = () => {
    // Only attempt to get the access token if the user is authenticated
    if (isAuthenticated) {
      return getAccessTokenSilently();
    }
    // Otherwise make API calls without an access token
    return Promise.resolve(undefined);
  };

  // Handle 401s from the Legacy API
  const onUnauthorized: LegacyIIQApiOptions['onUnauthorized'] = async () => {
    // Save user's current path to return them to it after login
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    setRedirect(currentPath);

    const signInMessage = encodeURIComponent(
      'Your session has expired. Please sign in again.'
    );

    await logout({
      logoutParams: {
        returnTo: `${window.location.origin}${insureiqPages.signIn.path}?msg=${signInMessage}`,
      },
    });
    return;
  };

  const legacyIIQApi = new LegacyIIQApi({
    getAuth0AccessToken,
    onUnauthorized,
  });

  const endpoints = getEndpoints({
    legacyIIQApi,
  });

  const contextValue: LegacyIIQApiContextProps = {
    legacyIIQApi,
    ...endpoints,
  };

  return (
    <LegacyIIQApiContext.Provider value={contextValue}>
      {isLoading ? <Loading /> : children}
    </LegacyIIQApiContext.Provider>
  );
}

/**
 * @deprecated Use the `useInsureIQApiClient` hook instead.
 */
export function useLegacyIIQApi() {
  const context = useContext(LegacyIIQApiContext);
  if (!context) {
    throw new Error(
      'useLegacyIIQApi must be used within an LegacyIIQApiProvider'
    );
  }
  return context;
}
